import React, { useState } from 'react';
import axios from 'axios';
import { Star, X, ChatCircleText, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal shown to a student a few seconds after they leave the Whereby live
 * room. Captures a 1–5 star rating + an optional anonymous comment.
 *
 * Why anonymous? The teacher wants brutally honest feedback ("no se oía",
 * "ese ejercicio me aburrió") that students would never write under their
 * real name. The backend stores ONLY the rating + comment + the audience
 * snapshot (CEFR level, learning goal, pace) so the teacher can segment by
 * audience without identifying anyone. See routes/classes.py for the hashing
 * & rate-limit logic.
 */
export default function ClassFeedbackModal({ open, onClose, classDate, isDe = true }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (rating < 1) {
      toast.error(isDe ? 'Bitte vergib zuerst eine Sternebewertung.' : 'Por favor, da una valoración primero.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/classes/feedback`, {
        rating,
        comment,
        class_date: classDate,
      }, { withCredentials: true });
      toast.success(isDe
        ? 'Vielen Dank — dein Feedback hilft, die Klassen besser zu machen.'
        : '¡Gracias! Tu feedback ayuda a mejorar las clases.');
      onClose(true);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        toast.message(isDe
          ? 'Du hast für diese Klasse bereits Feedback gegeben — danke!'
          : 'Ya enviaste feedback para esta clase, ¡gracias!');
        onClose(true);
      } else {
        toast.error(isDe ? 'Speichern fehlgeschlagen. Versuche es später nochmal.' : 'Error al guardar. Inténtalo más tarde.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => onClose(false);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3" data-testid="class-feedback-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#AB1C24] to-[#7a131a] text-white px-5 py-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="font-heading font-semibold text-lg leading-tight">
              {isDe ? 'Wie war die Klasse?' : '¿Cómo fue la clase?'}
            </h2>
            <p className="text-white/80 text-xs mt-0.5 flex items-center gap-1.5">
              <ShieldCheck size={14} weight="fill" />
              {isDe ? '100 % anonym — niemand sieht, wer geantwortet hat' : '100 % anónimo — nadie sabe quién ha respondido'}
            </p>
          </div>
          <button onClick={dismiss} className="text-white/80 hover:text-white" data-testid="class-feedback-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-[#57534E] leading-relaxed">
            {isDe
              ? 'Bitte hilf Alberto, die Klassen für dich besser zu machen. Schreib ehrlich, wenn etwas nicht funktioniert hat — z. B. wenn du Teile der Klasse nicht gut hören konntest oder wenn dir eine Übung nicht gefallen hat. Deine Antwort wird '
              : 'Por favor, ayuda a Alberto a mejorar las clases. Escribe con honestidad si algo no funcionó — por ejemplo si no se escuchó bien una parte de la clase o si algún ejercicio no te gustó. Tu respuesta se usa '}
            <span className="font-semibold text-[#292524]">
              {isDe ? 'völlig anonym verwendet' : 'de forma totalmente anónima'}
            </span>
            {isDe
              ? ', um die Inhalte besser auf dein Niveau und deine Ziele anzupassen.'
              : ', con el único fin de adaptar mejor las clases a tu nivel y tus objetivos.'}
          </p>

          {/* Star rating */}
          <div className="flex items-center justify-center gap-1.5 py-2" data-testid="class-feedback-stars">
            {[1, 2, 3, 4, 5].map(i => {
              const active = i <= (hover || rating);
              return (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(i)}
                  className="transition-transform hover:scale-110"
                  aria-label={`${i} ${isDe ? 'Sterne' : 'estrellas'}`}
                  data-testid={`class-feedback-star-${i}`}
                >
                  <Star size={36} weight={active ? 'fill' : 'regular'} className={active ? 'text-[#F59E0B]' : 'text-stone-300'} />
                </button>
              );
            })}
          </div>

          {/* Comment */}
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-[#78716C] flex items-center gap-1 mb-1">
              <ChatCircleText size={13} weight="fill" />
              {isDe ? 'Was sollten wir wissen?' : '¿Qué deberíamos saber?'}
              <span className="ml-1 normal-case font-normal text-stone-400">{isDe ? '(optional)' : '(opcional)'}</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder={isDe
                ? 'Beispiele: "Ab Minute 20 war der Ton schlecht", "Die Konjugationen-Übung war zu schnell", "Mehr Reise-Vokabular bitte"…'
                : 'Ejemplos: "A partir del minuto 20 se oía mal", "El ejercicio de conjugaciones fue muy rápido", "Más vocabulario de viajes, por favor"…'}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#AB1C24]/40"
              data-testid="class-feedback-comment"
            />
            <p className="text-[10px] text-stone-400 mt-1">
              {comment.length} / 2000
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-stone-50 px-5 py-3 flex items-center justify-between gap-3 border-t border-stone-200">
          <button
            onClick={dismiss}
            className="text-sm text-[#78716C] hover:text-[#292524] font-semibold"
            data-testid="class-feedback-skip"
          >
            {isDe ? 'Später' : 'Más tarde'}
          </button>
          <button
            onClick={submit}
            disabled={submitting || rating < 1}
            className="bg-[#AB1C24] hover:bg-[#8A151C] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg text-sm font-semibold"
            data-testid="class-feedback-submit"
          >
            {submitting ? (isDe ? 'Sende…' : 'Enviando…') : (isDe ? 'Anonym senden' : 'Enviar anónimo')}
          </button>
        </div>
      </div>
    </div>
  );
}
